"use client";

import { useId } from "react";
import { useToolNumber } from "@/components/tools/tool-ui";
import type { IntervalSet } from "@/lib/tool-kit/algebra/intervals";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import { linearScale, niceStep, ticksBetween } from "@/lib/tool-kit/math/plot";
import {
  formatStyled,
  type NumberStyle,
  toNumberR,
} from "@/lib/tool-kit/math/rational";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A number line with the solution set shaded: a filled circle for an included end, an
 * open circle for an excluded one, and an arrow where the set runs on to infinity.
 * Colours are attributes so the SVG looks the same wherever it is copied.
 */
const WIDTH = 480;
const HEIGHT = 96;
const AXIS_Y = 58;
const LEFT = 22;
const RIGHT = WIDTH - 22;
const SHADE = CHART_COLORS[0];
const UNSAFE_ID = /[^a-zA-Z0-9_-]/g;

function windowFor(values: number[]): [number, number] {
  if (values.length === 0) {
    return [-5, 5];
  }
  const low = Math.min(...values);
  const high = Math.max(...values);
  if (high - low < 2) {
    const middle = (low + high) / 2;
    return [middle - 3, middle + 3];
  }
  const pad = Math.max((high - low) * 0.4, 1.5);
  return [low - pad, high + pad];
}

export function NumberLine({
  set,
  title,
  style,
}: {
  set: IntervalSet;
  title: string;
  style: NumberStyle;
}) {
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const uid = useId().replace(UNSAFE_ID, "");
  const finite = set.flatMap((i) =>
    [i.lo, i.hi].flatMap((b) => (b ? [toNumberR(b.value)] : []))
  );
  const [min, max] = windowFor(finite);
  const x = linearScale(min, max, LEFT, RIGHT);
  const ticks = ticksBetween(min, max, niceStep(max - min, 8));
  return (
    <svg
      aria-labelledby={`nl-${uid}`}
      className="h-auto w-full max-w-xl"
      fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
      role="img"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id={`nl-${uid}`}>{title}</title>
      <rect fill={CHART_INK.background} height={HEIGHT} width={WIDTH} />
      <line
        stroke={CHART_INK.text}
        strokeWidth={1.5}
        x1={8}
        x2={WIDTH - 8}
        y1={AXIS_Y}
        y2={AXIS_Y}
      />
      <path d={`M8 ${AXIS_Y} l8 -4 v8 z`} fill={CHART_INK.text} />
      <path d={`M${WIDTH - 8} ${AXIS_Y} l-8 -4 v8 z`} fill={CHART_INK.text} />
      {ticks.map((tick) => (
        <g key={tick}>
          <line
            stroke={CHART_INK.text}
            x1={x(tick)}
            x2={x(tick)}
            y1={AXIS_Y - 5}
            y2={AXIS_Y + 5}
          />
          <text
            fill={CHART_INK.muted}
            fontSize={11}
            textAnchor="middle"
            x={x(tick)}
            y={AXIS_Y + 20}
          >
            {fmt(tick, { maximumFractionDigits: 3 }).replace("-", "−")}
          </text>
        </g>
      ))}
      {set.map((interval) => {
        const from = interval.lo ? x(toNumberR(interval.lo.value)) : 8;
        const to = interval.hi ? x(toNumberR(interval.hi.value)) : WIDTH - 8;
        return (
          <g key={`${from}-${to}`}>
            <line
              stroke={SHADE}
              strokeLinecap="round"
              strokeWidth={6}
              x1={from}
              x2={to}
              y1={AXIS_Y}
              y2={AXIS_Y}
            />
            {interval.lo ? null : (
              <path d={`M8 ${AXIS_Y} l12 -8 v16 z`} fill={SHADE} />
            )}
            {interval.hi ? null : (
              <path d={`M${WIDTH - 8} ${AXIS_Y} l-12 -8 v16 z`} fill={SHADE} />
            )}
            {[interval.lo, interval.hi].map((bound, index) =>
              bound ? (
                <g key={index === 0 ? "lo" : "hi"}>
                  <circle
                    cx={x(toNumberR(bound.value))}
                    cy={AXIS_Y}
                    fill={bound.closed ? SHADE : CHART_INK.background}
                    r={7}
                    stroke={SHADE}
                    strokeWidth={3}
                  />
                  <text
                    fill={CHART_INK.text}
                    fontSize={12}
                    fontWeight={600}
                    textAnchor="middle"
                    x={x(toNumberR(bound.value))}
                    y={AXIS_Y - 16}
                  >
                    {formatStyled(bound.value, style, mark)}
                  </text>
                </g>
              ) : null
            )}
          </g>
        );
      })}
    </svg>
  );
}
